import { Component, Inject } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatDividerModule } from '@angular/material/divider'
import { MatTooltipModule } from '@angular/material/tooltip'
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelDescription,
} from '@angular/material/expansion'
import { MatTab, MatTabGroup } from '@angular/material/tabs'

interface PassageiroGrupo {
  key: string
  sample: any
  passageiros: any[]
}

@Component({
  selector: 'modal-preview-logistics',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatTooltipModule,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    MatTabGroup,
    MatTab,
  ],
  templateUrl: './modal-preview-logistics.component.html',
  styleUrl: './modal-preview-logistics.component.scss',
})
export class ModalPreviewLogisticsComponent {
  contrato: any
  consultor: any
  itens: any[]
  abas: any[]
  fechamentoResumo: any
  coordenadores: any[]

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalPreviewLogisticsComponent>,
  ) {
    this.contrato = data?.contrato ?? null
    this.consultor = data?.consultor ?? null
    this.coordenadores = data?.coordenadores ?? []

    const resumo = data?.fechamentoResumo ?? null
    this.fechamentoResumo = resumo
      ? {
          ...resumo,
          trechos: [...(resumo.trechos ?? [])].sort(
            (a: any, b: any) =>
              this.prioridadeCategoria(a?.categoria, a?.tipoTrecho) -
              this.prioridadeCategoria(b?.categoria, b?.tipoTrecho),
          ),
        }
      : null

    // As abas espelham a planilha de logística 1:1 (cada grupo = 1 item de contrato = 1 veículo).
    // As colunas de voo/endereço só são exibidas quando algum passageiro da aba as possui.
    this.abas = (data?.abas ?? [])
      .map((aba: any) => ({
        ...aba,
        mostrarVoo: (aba.grupos ?? []).some((g: any) =>
          (g.passageiros ?? []).some((p: any) => p.companhia || p.voo || p.horarioVoo),
        ),
        mostrarEnderecoPax: (aba.grupos ?? []).some((g: any) =>
          (g.passageiros ?? []).some((p: any) => p.origem || p.destino),
        ),
      }))
      .sort((a: any, b: any) => this.prioridadeAba(a) - this.prioridadeAba(b))

    this.itens = (data?.itens ?? []).map((entry: any) => ({
      ...entry,
      ordensServico: (entry.ordensServico ?? []).map((os: any) => ({
        ...os,
        gruposPassageiros: this.agruparPassageiros(os.passageiros),
      })),
    }))
  }

  // Chegadas (In) vêm primeiro, depois saídas (Out), depois as demais.
  // O sort é estável, então a ordem original do backend é mantida dentro de cada faixa.
  private prioridadeAba(aba: any): number {
    return this.prioridadeCategoria(aba?.categoria, aba?.titulo)
  }

  private prioridadeCategoria(categoria: string | null | undefined, texto: string | null | undefined): number {
    const cat = (categoria || '').toLowerCase()
    const txt = (texto || '').toLowerCase()
    if (cat === 'aereoin' || /\bin\b/.test(txt)) return 0
    if (cat === 'aereoout' || /\bout\b/.test(txt)) return 1
    return 2
  }

  // Passageiros com o mesmo endereço de origem/destino, voo e horário são exibidos
  // como um único grupo (mesma lógica de "mesclar células" usada na planilha de logística).
  private agruparPassageiros(passageiros: any[]): PassageiroGrupo[] {
    const grupos = new Map<string, PassageiroGrupo>()

    for (const p of passageiros ?? []) {
      const key = [
        p.origemTipo, p.origemLogradouro, p.origemNumero, p.origemComplemento, p.origemBairro, p.origemCidade, p.origemCep, p.origemAeroSigla,
        p.destinoTipo, p.destinoLogradouro, p.destinoNumero, p.destinoComplemento, p.destinoBairro, p.destinoCidade, p.destinoCep, p.destinoAeroSigla,
        p.companhia, p.voo, p.horaPartida, p.horaChegada, p.vooHora, p.vooData, p.speaker,
      ].join('|')

      if (!grupos.has(key)) {
        grupos.set(key, { key, sample: p, passageiros: [] })
      }
      grupos.get(key)!.passageiros.push(p)
    }

    return Array.from(grupos.values()).sort((a, b) =>
      (a.sample.horaPartida || '').localeCompare(b.sample.horaPartida || ''),
    )
  }

  endereco(p: any, tipo: 'origem' | 'destino'): string {
    if (!p) return '-'
    const logradouro = p[`${tipo}Logradouro`]
    const numero = p[`${tipo}Numero`]
    const complemento = p[`${tipo}Complemento`]
    const bairro = p[`${tipo}Bairro`]
    const cidade = p[`${tipo}Cidade`]
    const aeroSigla = p[`${tipo}AeroSigla`]

    const endereco = [logradouro, numero].filter(Boolean).join(', ')
    const detalhes = [complemento, bairro].filter(Boolean).join(' - ')
    const aero = aeroSigla ? `(${aeroSigla})` : ''

    return [endereco, detalhes, cidade, aero].filter(Boolean).join(' · ') || '-'
  }

  vooInfo(p: any): string {
    if (!p?.companhia && !p?.voo) return '-'
    return `${p.companhia || ''} ${p.voo || ''}`.trim()
  }

  hora(value: string | null | undefined): string {
    return value?.substring(0, 5) || '-'
  }

  fmt(value: any): string {
    if (value == null) return '-'
    const n = typeof value === 'number' ? value : parseFloat(value)
    if (isNaN(n)) return '-'
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  }

  close() {
    this.dialogRef.close()
  }
}
